<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Registration } from '@/types/registration'
import { useStripePayment } from '@/composables/useStripePayment'

const props = defineProps<{
  registration: Registration
  orgSlug: string
  eventSlug: string
  raceSlug: string
  /** current_price_cents the participant saw on the summary (server value), to detect a price change. */
  seenPriceCents?: number
}>()

const { t } = useI18n()

const {
  isLoadingIntent,
  isProcessingPayment,
  paymentError,
  stripeReady,
  initializePayment,
  confirmPayment,
  destroy,
} = useStripePayment()

onMounted(() => {
  void initializePayment(props.registration.id, 'stripe-payment-element')
})

onUnmounted(() => {
  destroy()
})

// Tier name snapshotted by the backend on the base_price breakdown line.
const baseLabel = computed<string>(() => {
  const line = props.registration.price_breakdown.find((item) => item.type === 'base_price')
  return line?.tier_name
    ? t('registration.payment.base_price_label_tier', { tier: line.tier_name })
    : t('registration.payment.base_price_label')
})

// Both values come from the server; no tier logic here.
const priceChanged = computed<boolean>(
  () =>
    props.seenPriceCents !== undefined &&
    Math.round(parseFloat(props.registration.base_price) * 100) !== props.seenPriceCents,
)

function formatCurrency(amount: string): string {
  return `EUR ${parseFloat(amount).toFixed(2)}`
}

async function handlePay(): Promise<void> {
  const returnUrl = `${window.location.origin}/${props.orgSlug}/${props.eventSlug}/${props.raceSlug}/confirmation/${props.registration.confirmation_token}`
  await confirmPayment(returnUrl)
}
</script>

<template>
  <div class="space-y-6">
    <!-- Heading -->
    <div>
      <h2 class="text-xl font-bold text-slate-900 mb-1">{{ t('registration.payment.heading') }}</h2>
    </div>

    <!-- Success banner -->
    <div class="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-5 py-4">
      <svg class="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-sm text-emerald-700 font-medium">{{ t('registration.payment.registration_saved') }}</p>
    </div>

    <!-- Status badge -->
    <div class="flex items-center gap-2">
      <span class="text-sm text-slate-600">{{ t('registration.payment.status_label') }}:</span>
      <span class="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-xs font-semibold">
        <span class="w-1.5 h-1.5 rounded-full bg-amber-500" />
        {{ t('registration.payment.status_payment_pending') }}
      </span>
    </div>

    <p
      v-if="priceChanged"
      class="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800"
      role="status"
      data-testid="price-changed-notice"
    >
      {{ t('registration.payment.price_changed') }}
    </p>

    <!-- Cost breakdown card -->
    <div class="rounded-xl border border-slate-200 overflow-hidden">
      <div class="px-5 py-3 bg-slate-50 border-b border-slate-200">
        <h3 class="text-sm font-semibold text-slate-700">{{ t('registration.payment.cost_breakdown_heading') }}</h3>
      </div>
      <div class="px-5 py-4 space-y-3">
        <!-- Base price -->
        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-600">{{ baseLabel }}</span>
          <span class="font-medium text-slate-900">{{ formatCurrency(registration.base_price) }}</span>
        </div>

        <!-- Options total (only when > 0) -->
        <div
          v-if="parseFloat(registration.options_total) > 0"
          class="flex items-center justify-between text-sm"
        >
          <span class="text-slate-600">{{ t('registration.payment.options_total_label') }}</span>
          <span class="font-medium text-slate-900">{{ formatCurrency(registration.options_total) }}</span>
        </div>

        <!-- Option / checkbox line items from price_breakdown -->
        <template v-for="(item, i) in registration.price_breakdown" :key="i">
          <div
            v-if="item.type === 'option' || item.type === 'checkbox'"
            class="flex items-center justify-between text-xs text-slate-500 pl-3"
          >
            <span>{{ item.type === 'option' ? `${item.field_label}: ${item.option_label}` : item.field_label }}</span>
            <span>+ EUR {{ parseFloat(item.amount).toFixed(2) }}</span>
          </div>
        </template>

        <!-- Voucher discount (only when > 0) -->
        <div
          v-if="parseFloat(registration.voucher_discount) > 0"
          class="flex items-center justify-between text-sm"
        >
          <span class="text-slate-600">{{ t('registration.payment.voucher_discount_label') }}</span>
          <span class="font-medium text-emerald-600">- {{ formatCurrency(registration.voucher_discount) }}</span>
        </div>

        <!-- Divider + total -->
        <div class="border-t border-slate-200 pt-3 flex items-center justify-between">
          <span class="text-sm font-bold text-slate-800">{{ t('registration.payment.total_label') }}</span>
          <span class="text-lg font-bold text-emerald-600">{{ formatCurrency(registration.total_amount) }}</span>
        </div>
      </div>
    </div>

    <!-- Stripe Payment Element mount point -->
    <div class="rounded-xl border border-slate-200 overflow-hidden">
      <div class="px-5 py-4">
        <!-- Loading skeleton while Stripe initialises -->
        <div v-if="isLoadingIntent" class="space-y-3 animate-pulse">
          <p class="text-sm text-slate-500 text-center">{{ t('registration.payment.initializing') }}</p>
          <div class="h-10 bg-slate-100 rounded-md" />
          <div class="h-10 bg-slate-100 rounded-md" />
          <div class="h-10 bg-slate-100 rounded-md" />
        </div>

        <!-- Stripe mounts the Payment Element here -->
        <div id="stripe-payment-element" :class="{ hidden: isLoadingIntent }" />
      </div>
    </div>

    <!-- Error banner -->
    <div
      v-if="paymentError"
      class="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600"
    >
      {{ paymentError.includes(' ') ? paymentError : t(paymentError) }}
    </div>

    <!-- Pay button + Stripe badge -->
    <div class="space-y-3">
      <button
        type="button"
        :disabled="isLoadingIntent || isProcessingPayment || !stripeReady"
        class="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-3 text-sm transition-colors"
        @click="handlePay"
      >
        <svg
          v-if="isProcessingPayment"
          class="animate-spin h-4 w-4 text-white"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span v-if="isProcessingPayment">{{ t('registration.payment.processing') }}</span>
        <span v-else>{{ t('registration.payment.pay_now', { amount: parseFloat(registration.total_amount).toFixed(2) }) }}</span>
      </button>

      <!-- Stripe security note -->
      <p class="flex items-center justify-center gap-1.5 text-xs text-slate-400">
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        {{ t('registration.payment.secured_by_stripe') }}
      </p>
    </div>
  </div>
</template>
